/**
 * SPEC-20260507-0136 (Fase 3): editor de saga definition.
 *
 * Layout 2-pane: catálogo à esquerda + lista de steps composta no centro.
 * Drag-drop reorder via @dnd-kit/sortable. ConditionBuilder inline por step.
 *
 * MVP funcional: drag-drop reorder + condition simples (eq+exists). Mapping
 * complexo / properties panel ficam pra polish posterior.
 */

import { useState } from "react";
import { DndContext, closestCenter, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import type { SagaStepDefinition, StepCondition } from "../../types/saga-definition";
import { StepCatalogSidebar } from "./StepCatalogSidebar";
import { ScriptStepEditor } from "./ScriptStepEditor";
import { HttpInvokeStepEditor } from "./HttpInvokeStepEditor";
import type { StepCatalogEntry } from "../../types/saga-definition";
import { Button } from "../ui/button";
import { cn } from "../../utils/cn";

export interface SagaDefinitionEditorProps {
  catalog: StepCatalogEntry[];
  initialSteps?: SagaStepDefinition[];
  initialName?: string;
  initialDescription?: string;
  /** Callback ao salvar (cria nova versão via POST). */
  onSave(payload: {
    name: string;
    description: string;
    steps: SagaStepDefinition[];
  }): Promise<void>;
  /** Read-only quando true (preview de versão active/archived). */
  readOnly?: boolean;
}

export function SagaDefinitionEditor({
  catalog,
  initialSteps = [],
  initialName = "",
  initialDescription = "",
  onSave,
  readOnly = false,
}: SagaDefinitionEditorProps) {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [steps, setSteps] = useState<SagaStepDefinition[]>(initialSteps);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function generateId(): string {
    return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  }

  function addStep(type: string) {
    if (readOnly) return;
    setSteps((prev) => [...prev, { id: generateId(), type }]);
  }

  function removeStep(id: string) {
    setSteps((prev) => prev.filter((s) => s.id !== id));
  }

  function updateStep(id: string, patch: Partial<SagaStepDefinition>) {
    setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setSteps((prev) => {
      const oldIdx = prev.findIndex((s) => s.id === active.id);
      const newIdx = prev.findIndex((s) => s.id === over.id);
      return arrayMove(prev, oldIdx, newIdx);
    });
  }

  async function onSubmit() {
    setErr(null);
    if (!name.trim()) return setErr("name é obrigatório");
    if (steps.length === 0) return setErr("Adicione ao menos 1 step");
    setSaving(true);
    try {
      await onSave({ name: name.trim(), description: description.trim(), steps });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className={
        readOnly
          ? "" // sem grid em readOnly: ocupa largura total
          : "grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4"
      }
    >
      {!readOnly && <StepCatalogSidebar catalog={catalog} onAdd={addStep} />}

      <div className="rounded-md border border-border bg-surface p-4 space-y-4">
        {!readOnly && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-1">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">
                Nome <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="OnboardTenant"
                disabled={!!initialName}
                className="w-full px-2 py-1.5 text-sm border border-border rounded bg-bg disabled:bg-surface-subtle disabled:text-muted-foreground font-mono-tight"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">
                Descrição
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Breve descrição..."
                className="w-full px-2 py-1.5 text-sm border border-border rounded bg-bg"
              />
            </div>
          </div>
        )}

        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">
            Steps ({steps.length})
          </p>
          {steps.length === 0 ? (
            <div className="text-xs text-muted-foreground italic py-6 text-center border border-dashed border-border-subtle rounded">
              {readOnly ? "Sem steps." : "Adicione steps do catálogo à esquerda."}
            </div>
          ) : (
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                <ol className="space-y-2">
                  {steps.map((step, idx) => (
                    <SortableStepItem
                      key={step.id}
                      step={step}
                      index={idx}
                      catalogEntry={catalog.find((c) => c.type === step.type)}
                      onRemove={() => removeStep(step.id)}
                      onUpdate={(patch) => updateStep(step.id, patch)}
                      readOnly={readOnly}
                    />
                  ))}
                </ol>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {err && <p className="text-xs text-danger">{err}</p>}

        {!readOnly && (
          <div className="flex justify-end gap-2 pt-3 border-t border-border-subtle">
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={onSubmit}
              disabled={saving || steps.length === 0}
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {saving ? "Salvando…" : initialName ? "Salvar como nova versão" : "Criar v1"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

interface SortableStepItemProps {
  step: SagaStepDefinition;
  index: number;
  catalogEntry: StepCatalogEntry | undefined;
  onRemove: () => void;
  onUpdate: (patch: Partial<SagaStepDefinition>) => void;
  readOnly: boolean;
}

function SortableStepItem({ step, index, catalogEntry, onRemove, onUpdate, readOnly }: SortableStepItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });
  const [expanded, setExpanded] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  function toggle() {
    setExpanded((v) => !v);
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "rounded-md border bg-bg overflow-hidden transition-colors",
        expanded ? "border-primary/40 shadow-sm" : "border-border",
      )}
    >
      {/* Header clicável — toggle expand. Não pode ser <button> porque dnd-kit
          listeners precisam receber pointerdown nativamente; aninhamento
          interactive-em-interactive também é HTML inválido. Usamos div com
          role=button + onKeyDown pra acessibilidade. */}
      <div
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
        className={cn(
          "w-full flex items-center gap-2 p-2.5 text-left cursor-pointer transition-colors select-none",
          "hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
          expanded && "bg-surface-subtle/40",
        )}
        aria-expanded={expanded}
        aria-controls={`step-body-${step.id}`}
      >
        <span className="text-muted-foreground shrink-0" aria-hidden>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>
        {!readOnly && (
          /* Drag handle: dnd-kit listeners são spread aqui. Click event é
             interceptado pra não toggle expand quando user só clica (sem drag). */
          <span
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground shrink-0 touch-none"
            aria-label="reordenar"
            title="Arraste pra reordenar"
          >
            <GripVertical size={14} />
          </span>
        )}
        <span className="text-xs font-mono text-muted-foreground w-6 tabular-nums shrink-0">
          #{index}
        </span>
        <code className="font-mono-tight text-sm flex-1 truncate">{step.type}</code>
        {catalogEntry?.isCompensable && (
          <span className="text-[10px] text-muted-foreground italic shrink-0">compensable</span>
        )}
        {step.condition && (
          <span className="text-[10px] bg-warning-soft text-warning-soft-fg px-1.5 py-0.5 rounded font-mono-tight shrink-0">
            cond
          </span>
        )}
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">
          {expanded ? "fechar" : "detalhes"}
        </span>
        {!readOnly && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="text-muted-foreground hover:text-danger shrink-0 p-1 -m-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger/40"
            aria-label="remover step"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {expanded && (
        <div
          id={`step-body-${step.id}`}
          className="px-3 pb-3 pt-2 border-t border-border-subtle space-y-2 text-xs"
        >
          {catalogEntry && (
            <p className="text-muted-foreground italic">{catalogEntry.description}</p>
          )}
          <ConditionBuilder
            condition={step.condition}
            onChange={(condition) => onUpdate({ condition })}
            readOnly={readOnly}
          />
          {/* SPEC-20260507-1400 (Fase 4): renderiza ScriptStepEditor pra script.exec. */}
          {step.type === "script.exec" && (
            <ScriptStepEditor step={step} onUpdate={onUpdate} readOnly={readOnly} />
          )}
          {/* SPEC-20260507-0136 (Fase 3) — extensão: editor tipado pra http.invoke. */}
          {step.type === "http.invoke" && (
            <HttpInvokeStepEditor step={step} onUpdate={onUpdate} readOnly={readOnly} />
          )}
        </div>
      )}
    </li>
  );
}

interface ConditionBuilderProps {
  condition?: StepCondition;
  onChange(c: StepCondition | undefined): void;
  readOnly: boolean;
}

function ConditionBuilder({ condition, onChange, readOnly }: ConditionBuilderProps) {
  if (!condition && readOnly) return null;
  return (
    <div className="rounded border border-border-subtle bg-surface-subtle/30 p-2">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1">
        Condição (executa só se ...)
      </p>
      {!condition ? (
        <button
          type="button"
          disabled={readOnly}
          onClick={() => onChange({ op: "exists", path: "input." })}
          className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          + adicionar condição
        </button>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <select
            disabled={readOnly}
            value={condition.op}
            onChange={(e) => {
              const op = e.target.value as "eq" | "exists";
              if (op === "eq" && condition.op !== "eq") {
                onChange({ op: "eq", path: condition.path, value: "" });
              } else if (op === "exists" && condition.op !== "exists") {
                onChange({ op: "exists", path: condition.path });
              }
            }}
            className="px-1.5 py-1 text-xs border border-border rounded bg-bg font-mono-tight"
          >
            <option value="eq">eq</option>
            <option value="exists">exists</option>
          </select>
          <input
            type="text"
            disabled={readOnly}
            value={condition.path}
            onChange={(e) =>
              onChange({ ...condition, path: e.target.value } as StepCondition)
            }
            placeholder="input.billingTier"
            className="flex-1 min-w-[150px] px-2 py-1 text-xs border border-border rounded bg-bg font-mono-tight"
          />
          {condition.op === "eq" && (
            <input
              type="text"
              disabled={readOnly}
              value={String(condition.value ?? "")}
              onChange={(e) => onChange({ op: "eq", path: condition.path, value: e.target.value })}
              placeholder="pro"
              className="w-32 px-2 py-1 text-xs border border-border rounded bg-bg font-mono-tight"
            />
          )}
          {!readOnly && (
            <button
              type="button"
              onClick={() => onChange(undefined)}
              className="text-muted-foreground hover:text-danger"
              aria-label="remover condição"
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
